import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CreditCard, ClipboardList, Settings, Bell, Receipt,
  Info, LogOut, Download, FileText, Shield, ShieldCheck,
  Calendar, Dumbbell, Ruler, Heart, Moon, ChevronRight,
  Flag, Trophy, BarChart3, Newspaper, Users
} from 'lucide-react';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { updatePreferences, fetchGroupChatInvitePrefs, updateGroupChatInvitePrefs } from '../services/api';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const tierIdx = user?.membership_tier || 0;
  const survey = user?.survey_data || {};
  const [digestPrefs, setDigestPrefs] = useState(user?.preferences || {
    weekly_digest: true, challenge_updates: false, goal_reminders: true, score_changes: true,
  });
  const [allowGroupInvites, setAllowGroupInvites] = useState(false);

  useEffect(() => {
    fetchGroupChatInvitePrefs()
      .then((r) => setAllowGroupInvites(!!r.allow_group_invites))
      .catch(() => {});
  }, []);

  const toggleGroupInvites = async () => {
    const next = !allowGroupInvites;
    setAllowGroupInvites(next);
    try {
      await updateGroupChatInvitePrefs(next);
    } catch (err) {
      setAllowGroupInvites(!next);
      console.error('Failed to update invite prefs', err);
    }
  };

  const togglePref = async (key) => {
    const updated = { ...digestPrefs, [key]: !digestPrefs[key] };
    setDigestPrefs(updated);
    try {
      await updatePreferences(updated);
    } catch (err) {
      console.error('Failed to update preferences', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 40px' }}>
      {/* INTRO VIDEO (members-only, compact portrait) */}
      <div data-testid="profile-intro-video-wrap" style={{ marginBottom: 16 }}>
        <VideoWithSoundToggle
          src="/media/profile-intro.mp4"
          testId="profile-intro-video"
          aspectRatio="9 / 16"
          maxWidth={140}
          objectFit="contain"
        />
      </div>

      <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Profile</h1>

      {/* Profile Card */}
      <div style={{ textAlign: 'center', backgroundColor: COLORS.surface, borderRadius: 24, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        {user.picture ? (
          <img src={user.picture} alt={user.name} style={{ width: 72, height: 72, borderRadius: 36, objectFit: 'cover', margin: '0 auto 12px', display: 'block' }} />
        ) : (
          <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif' }}>{(user.name || 'U')[0]}</span>
          </div>
        )}
        <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{user.name}</div>
        <div style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 4 }}>{user.email}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: TIER_COLORS[tierIdx] || COLORS.primary, padding: '6px 14px', borderRadius: 9999, marginTop: 12 }}>
          <Shield size={14} color="#FFF" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#FFF' }}>{TIER_NAMES[tierIdx] || 'Free'} Member</span>
        </div>
      </div>

      {/* Health Profile */}
      {user.survey_completed && Object.keys(survey).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Health Profile</h2>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: '0 16px' }}>
            {[
              { icon: Calendar, label: 'Age', value: `${survey.age || '-'} years` },
              { icon: Dumbbell, label: 'Weight', value: `${survey.weight || '-'} lbs` },
              { icon: Ruler, label: 'Height', value: `${survey.height || '-'} in` },
              { icon: Dumbbell, label: 'Exercise Level', value: `${survey.exercise_level || '-'}/7` },
              { icon: Heart, label: 'Health Perception', value: `${survey.health_perception || '-'}/7` },
              { icon: Moon, label: 'Sleep', value: `${survey.hours_sleep || '-'} hrs/night` },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < 5 ? `1px solid ${COLORS.border}` : 'none' }}>
                <item.icon size={18} color={COLORS.primary} />
                <span style={{ flex: 1, fontSize: 14, color: COLORS.textSecondary }}>{item.label}</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Settings</h2>
        {[
          { icon: CreditCard, color: COLORS.primary, label: 'Membership & Plans', link: '/dashboard/profile' },
          { icon: ClipboardList, color: COLORS.secondary, label: 'Retake Health Survey', link: '/dashboard/profile' },
          { icon: ShieldCheck, color: '#0F766E', label: 'Medical Records (HIPAA-Secure)', link: '/dashboard/profile/medical-records', testid: 'profile-link-medical-records' },
          { icon: Dumbbell, color: '#4682B4', label: 'Body Composition (Smart-Scale Metrics)', link: '/dashboard/profile/body-composition', testid: 'profile-link-body-composition' },
          { icon: Bell, color: '#5C6BC0', label: 'Notifications', link: '/dashboard/profile' },
          { icon: Receipt, color: '#FF9800', label: 'Order History', link: '/dashboard/store' },
        ].map((item, i) => (
          <Link key={i} to={item.link} data-testid={item.testid} className="settings-row" style={{ display: 'flex', alignItems: 'center', gap: 12, backgroundColor: COLORS.surface, borderRadius: 8, padding: 16, marginBottom: 8, textDecoration: 'none' }}>
            <item.icon size={22} color={item.color} />
            <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: COLORS.textPrimary }}>{item.label}</span>
            <ChevronRight size={18} color={COLORS.textSecondary} />
          </Link>
        ))}
      </div>

      {/* Export */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Health Report</h2>
        <div style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <FileText size={24} color={COLORS.primary} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>Export Health Report (PDF)</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2, lineHeight: 1.4 }}>Download a comprehensive report with scores, trends, and wearable data</div>
            </div>
          </div>
          <button style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: COLORS.primary, borderRadius: 9999, padding: 12, border: 'none', cursor: 'pointer' }}>
            <Download size={16} color="#FFF" />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#FFF' }}>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Digest Preferences */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Email & Digest Preferences</h2>
        {[
          { key: 'weekly_digest', label: 'Weekly Health Digest', desc: 'Weekly email summary of health scores & tips', icon: Newspaper, color: '#5C6BC0' },
          { key: 'challenge_updates', label: 'Challenge Updates', desc: 'Notifications for challenge progress', icon: Trophy, color: '#FF9800' },
          { key: 'goal_reminders', label: 'Goal Reminders', desc: 'Reminders about your health goals', icon: Flag, color: '#4CAF50' },
          { key: 'score_changes', label: 'Score Changes', desc: 'Alerts for significant score changes', icon: BarChart3, color: COLORS.primary },
        ].map(pref => (
          <button key={pref.key} onClick={() => togglePref(pref.key)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', backgroundColor: COLORS.surface, borderRadius: 8, padding: 16, marginBottom: 8, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <pref.icon size={20} color={pref.color} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>{pref.label}</div>
              <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>{pref.desc}</div>
            </div>
            <div style={{ width: 46, height: 26, borderRadius: 13, backgroundColor: digestPrefs[pref.key] ? COLORS.primary : COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', padding: 3, transition: 'background-color 0.2s' }}>
              <div style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: '#FFF', transition: 'margin-left 0.2s', marginLeft: digestPrefs[pref.key] ? 20 : 0 }} />
            </div>
          </button>
        ))}
      </div>

      {/* Community & Privacy */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Community & Privacy</h2>
        <button
          data-testid="toggle-group-invites"
          onClick={toggleGroupInvites}
          style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', backgroundColor: COLORS.surface, borderRadius: 8, padding: 16, marginBottom: 8, border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <Users size={20} color={COLORS.primary} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>Allow Group-Blog Invitations</div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2, lineHeight: 1.45 }}>
              When ON, you appear in the member directory and other members can invite you to their group blogs. When OFF, you're invisible and only reachable by email invite from a room owner.
            </div>
          </div>
          <div style={{ width: 46, height: 26, borderRadius: 13, backgroundColor: allowGroupInvites ? COLORS.primary : COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', padding: 3, transition: 'background-color 0.2s' }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: '#FFF', transition: 'margin-left 0.2s', marginLeft: allowGroupInvites ? 20 : 0 }} />
          </div>
        </button>
      </div>

      {/* Info */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Information</h2>
        <div className="settings-row" style={{ display: 'flex', alignItems: 'center', gap: 12, backgroundColor: COLORS.surface, borderRadius: 8, padding: 16, cursor: 'pointer' }}>
          <Info size={22} color={COLORS.primary} />
          <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: COLORS.textPrimary }}>About, Privacy, Terms & More</span>
          <ChevronRight size={18} color={COLORS.textSecondary} />
        </div>
      </div>

      {/* Logout */}
      <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, borderRadius: 9999, border: `1.5px solid ${COLORS.error}`, backgroundColor: 'transparent', cursor: 'pointer', marginTop: 8 }}>
        <LogOut size={20} color={COLORS.error} />
        <span style={{ fontSize: 16, fontWeight: 600, color: COLORS.error }}>Sign Out</span>
      </button>

      <div style={{ textAlign: 'center', fontSize: 12, color: COLORS.border, marginTop: 16 }}>ThriveLonger.ai v2.0.0</div>
    </div>
  );
}
